// Toast — iOS HIG toast/snackbar system
// Variants: success, error, info, warning
// Auto-dismisses after duration. Tap to dismiss. Stacks.

import { useEffect, useState, useCallback } from 'react';

export type ToastVariant = 'success' | 'error' | 'info' | 'warning';

export interface ToastMessage {
  id: string;
  variant: ToastVariant;
  title: string;
  description?: string;
  duration?: number;  // ms
  action?: { label: string; onClick: () => void };
}

let listeners: ((t: ToastMessage) => void)[] = [];

export function toast(t: Omit<ToastMessage, 'id'>) {
  const msg: ToastMessage = { id: 't_' + Date.now() + '_' + Math.random().toString(36).slice(2, 7), duration: 3500, ...t };
  listeners.forEach((l) => l(msg));
  return msg.id;
}

toast.success = (title: string, description?: string) => toast({ variant: 'success', title, description });
toast.error   = (title: string, description?: string) => toast({ variant: 'error', title, description, duration: 5000 });
toast.info    = (title: string, description?: string) => toast({ variant: 'info', title, description });
toast.warning = (title: string, description?: string) => toast({ variant: 'warning', title, description, duration: 4500 });

const VARIANT_META: Record<ToastVariant, { color: string; bg: string; icon: string }> = {
  success: { color: '#34C759', bg: 'rgba(52,199,89,0.15)',  icon: '✓' },
  error:   { color: '#FF3B30', bg: 'rgba(255,59,48,0.15)',  icon: '⛔' },
  info:    { color: '#007AFF', bg: 'rgba(0,122,255,0.15)',  icon: 'ℹ️' },
  warning: { color: '#FF9500', bg: 'rgba(255,149,0,0.15)',  icon: '⚠️' },
};

export function ToastContainer() {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const remove = useCallback((id: string) => {
    setToasts((t) => t.filter((x) => x.id !== id));
  }, []);

  useEffect(() => {
    const handler = (msg: ToastMessage) => {
      setToasts((t) => [...t, msg]);
      if (msg.duration) {
        setTimeout(() => remove(msg.id), msg.duration);
      }
    };
    listeners.push(handler);
    return () => { listeners = listeners.filter((l) => l !== handler); };
  }, [remove]);

  return (
    <div className="fixed top-12 left-1/2 -translate-x-1/2 z-[100] flex flex-col items-center gap-2 pointer-events-none" style={{ paddingTop: 'env(safe-area-inset-top)' }} data-testid="toast-container">
      {toasts.map((t) => {
        const m = VARIANT_META[t.variant];
        return (
          <div
            key={t.id}
            data-testid={`toast-${t.variant}`}
            onClick={() => remove(t.id)}
            className="pointer-events-auto ios26-glass rounded-[14px] px-4 py-3 min-w-[280px] max-w-[400px] haptic"
            style={{ background: 'rgba(28,28,30,0.92)', backdropFilter: 'saturate(180%) blur(24px)', color: 'white', boxShadow: '0 8px 24px rgba(0,0,0,0.24)' }}
          >
            <div className="flex items-start gap-2.5">
              <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[14px] font-bold" style={{ backgroundColor: m.color }}>{m.icon}</div>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-semibold text-white">{t.title}</div>
                {t.description && <div className="text-[11px] text-white/70 mt-0.5">{t.description}</div>}
                {t.action && (
                  <button
                    onClick={(e) => { e.stopPropagation(); t.action!.onClick(); remove(t.id); }}
                    data-testid="toast-action"
                    className="mt-1.5 text-[12px] font-semibold text-[#0A84FF] active:opacity-40"
                  >{t.action.label}</button>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
